import {
  CART_CHANGED_EVENT,
  CART_STORAGE_KEY,
  CART_TTL_DAYS,
  CART_VERSION,
  EMPTY_CART,
  type Cart,
  type CartItem,
} from './types'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export function readCart(): Cart {
  if (typeof window === 'undefined') return { ...EMPTY_CART }
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return { ...EMPTY_CART }
    const parsed = JSON.parse(raw) as Partial<Cart>
    if (parsed.version !== CART_VERSION || !Array.isArray(parsed.items)) {
      window.localStorage.removeItem(CART_STORAGE_KEY)
      return { ...EMPTY_CART }
    }
    const ageMs = Date.now() - Date.parse(parsed.updatedAt ?? '')
    if (Number.isFinite(ageMs) && ageMs > CART_TTL_DAYS * MS_PER_DAY) {
      window.localStorage.removeItem(CART_STORAGE_KEY)
      return { ...EMPTY_CART }
    }
    return {
      version: CART_VERSION,
      items: parsed.items.filter(isValidItem),
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
    }
  } catch {
    return { ...EMPTY_CART }
  }
}

export function writeCart(cart: Cart): void {
  if (typeof window === 'undefined') return
  const next: Cart = {
    version: CART_VERSION,
    items: cart.items,
    updatedAt: new Date().toISOString(),
  }
  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent<Cart>(CART_CHANGED_EVENT, { detail: next }))
  syncHtmlAttribute(next)
}

export function clearCart(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(CART_STORAGE_KEY)
  window.dispatchEvent(new CustomEvent<Cart>(CART_CHANGED_EVENT, { detail: { ...EMPTY_CART } }))
  syncHtmlAttribute({ ...EMPTY_CART })
}

export function cartCount(cart: Cart): number {
  return cart.items.reduce((n, i) => n + i.qty, 0)
}

function isValidItem(value: unknown): value is CartItem {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.sku === 'string' &&
    typeof v.qty === 'number' &&
    typeof v.price === 'number' &&
    typeof v.name === 'string'
  )
}

function syncHtmlAttribute(cart: Cart): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-al-cart-count', String(cartCount(cart)))
}
